/**
 * App-wide notification inbox. localStorage is the cache; the server
 * clock (Chunk 5) is the source for pings that arrive while closed.
 *
 * Type ids, icons, and prefixes live in src/notifications/.
 */

import { NOTIFICATION_TYPE, getNotificationType } from '../notifications';
import { ClockAPI } from './api/clockApi';
import { isAvailableEvent, stableInboxItemId } from './inboxIds';

export { stableInboxItemId } from './inboxIds';

export { INBOX_TYPE_AVAILABLE, NOTIFICATION_TYPE } from '../notifications';

export const INBOX_CHANGED = 'fokus:inbox-changed';
export const INBOX_TARGETS_GONE = 'fokus:inbox-targets-gone';
export const INBOX_TARGETS_RESTORED = 'fokus:inbox-targets-restored';

export function formatCampaignBreadcrumb(parents = [], campaignName = '') {
  return [...(parents || []).map((p) => p?.name).filter(Boolean), campaignName]
    .filter(Boolean)
    .join(' / ');
}

export function formatNotificationCopy(type, { missionName, campaignPath } = {}) {
  const mission = String(missionName || '').trim() || 'Untitled mission';
  const { prefix } = getNotificationType(type);
  return {
    title: `${prefix} ${mission}`,
    body: campaignPath || '',
    missionName: mission,
  };
}

export function formatAvailableReminder({ missionName, campaignPath }) {
  return formatNotificationCopy(NOTIFICATION_TYPE.MISSION_AVAILABLE, {
    missionName,
    campaignPath,
  });
}

/** Split a stored inbox row into prefix / mission / path for the panel. */
export function getInboxItemDisplay(item) {
  if (item?.type === NOTIFICATION_TYPE.WORKSPACE_INVITE) {
    const { prefix } = getNotificationType(item.type);
    return {
      prefix,
      mission: item.missionName || 'this brain',
      path: '',
      note: '',
      icon: null,
    };
  }
  const inferredType =
    item?.type ||
    (item && (
      item.title === "It's time"
      || /^It's time to start mission:/i.test(item.title || '')
      || /^Now available:/i.test(item.title || '')
    )
      ? NOTIFICATION_TYPE.MISSION_AVAILABLE
      : null);
  const { prefix } = getNotificationType(inferredType);
  if (!item) {
    return { prefix, mission: 'Untitled mission', path: '', note: '', icon: null };
  }
  const title = String(item.title || '');
  if (title === "It's time") {
    return {
      prefix,
      mission: item.missionName || item.body || 'Untitled mission',
      path: item.campaignName || '',
      note: String(item.note || '').trim(),
      icon: item.missionIcon || null,
    };
  }
  const namedMatch = title.match(
    /^(?:Now available:|It's time to start mission:|Reminder: time to start:|Reminder:|Wait for:|This mission is available:|Notification:)\s*(.*)$/i
  );
  const mission = item.missionName || namedMatch?.[1] || title || 'Untitled mission';
  const path = String(item.body || '').replace(/^in\s+/i, '').trim() || item.campaignName || '';
  return {
    prefix,
    mission,
    path,
    note: String(item.note || '').trim(),
    icon: item.missionIcon || null,
  };
}

export function notifyInboxTargetsGone(detail) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(INBOX_TARGETS_GONE, { detail: detail || {} }));
}

export function notifyInboxTargetsRestored(detail) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(INBOX_TARGETS_RESTORED, { detail: detail || {} }));
}

/** Newest kept; older rows drop. Enough for the panel; not a full archive. */
const MAX_ITEMS = 80;

/** Ids removed locally until undo. Kept in storage so a poll or refresh cannot put them back. */
function deletedIdsKey(userEmail, workspaceId) {
  return `${inboxStorageKey(userEmail, workspaceId)}:deleted`;
}

function readDeletedIds(userEmail, workspaceId) {
  if (!canUseStorage()) return new Set();
  try {
    const raw = localStorage.getItem(deletedIdsKey(userEmail, workspaceId));
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function writeDeletedIds(userEmail, workspaceId, ids) {
  if (!canUseStorage()) return;
  const key = deletedIdsKey(userEmail, workspaceId);
  if (!ids.size) {
    localStorage.removeItem(key);
    return;
  }
  localStorage.setItem(key, JSON.stringify([...ids]));
}

export function isInboxItemTombstoned(userEmail, workspaceId, id) {
  return readDeletedIds(userEmail, workspaceId).has(id);
}

export function clearInboxTombstones() {
  if (!canUseStorage()) return;
  const keys = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key && key.endsWith(':deleted')) keys.push(key);
  }
  keys.forEach((key) => localStorage.removeItem(key));
}

export function inboxStorageKey(userEmail, workspaceId) {
  const user = String(userEmail || 'anon').toLowerCase();
  const room = workspaceId || 'personal';
  return `fokus:inbox:${user}:${room}`;
}

function canUseStorage() {
  return typeof localStorage !== 'undefined';
}

export function loadInbox(userEmail, workspaceId) {
  if (!canUseStorage()) return [];
  try {
    const raw = localStorage.getItem(inboxStorageKey(userEmail, workspaceId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveInbox(userEmail, workspaceId, items) {
  if (!canUseStorage()) return;
  try {
    localStorage.setItem(
      inboxStorageKey(userEmail, workspaceId),
      JSON.stringify((items || []).slice(0, MAX_ITEMS))
    );
  } catch {
    /* quota / private mode */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(INBOX_CHANGED));
  }
}

export function mergeInboxItems(local = [], remote = []) {
  const byId = new Map();
  for (const item of local || []) {
    if (item?.id) byId.set(item.id, item);
  }
  for (const item of remote || []) {
    if (!item?.id) continue;
    const existing = byId.get(item.id);
    if (!existing) {
      byId.set(item.id, item);
      continue;
    }
    byId.set(item.id, {
      ...item,
      ...existing,
      read: Boolean(existing.read || item.read),
      targetGone: Boolean(existing.targetGone || item.targetGone),
      targetGoneKind: existing.targetGoneKind || item.targetGoneKind,
    });
  }
  return [...byId.values()]
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
    .slice(0, MAX_ITEMS);
}

function hasUnreadDuplicate(items, item) {
  return items.some((existing) => {
    if (existing.read) return false;
    if (existing.campaignId !== item.campaignId || existing.missionId !== item.missionId) {
      return false;
    }
    if (isAvailableEvent(existing.type) && isAvailableEvent(item.type)) return true;
    if (existing.type !== item.type) return false;
    if (
      item.type === NOTIFICATION_TYPE.REMINDER_AT_TIME
      || item.type === NOTIFICATION_TYPE.WAIT_FOR
    ) {
      if ((existing.reminderId || '') !== (item.reminderId || '')) return false;
      return (existing.occurrenceAt || '') === (item.occurrenceAt || '');
    }
    return true;
  });
}

/**
 * Write a mission ping. Dedupes unread available events for the same mission.
 */
export function notifyMissionInbox({
  userEmail,
  workspaceId,
  type,
  task,
  campaignId,
  campaignName,
  breadcrumbPath = [],
  reminderId,
  occurrenceAt,
  note,
}) {
  const campaignPath = formatCampaignBreadcrumb(breadcrumbPath, campaignName);
  const copy = formatNotificationCopy(type, {
    missionName: task?.name,
    campaignPath,
  });
  return addInboxItem(userEmail, workspaceId, {
    type,
    title: copy.title,
    body: copy.body,
    campaignId,
    campaignName: campaignPath,
    missionName: copy.missionName,
    missionIcon: task?.icon || null,
    missionId: task?.id,
    reminderId,
    occurrenceAt,
    note,
  });
}

export function addInboxItem(userEmail, workspaceId, item) {
  const items = loadInbox(userEmail, workspaceId);
  const id = item.id || stableInboxItemId(item);
  if (items.some((existing) => existing.id === id)) return null;
  if (hasUnreadDuplicate(items, item)) return null;
  const stored = {
    id,
    type: item.type,
    title: item.title,
    body: item.body || '',
    campaignId: item.campaignId,
    campaignName: item.campaignName || '',
    missionName: item.missionName || '',
    missionIcon: item.missionIcon || null,
    missionId: item.missionId,
    reminderId: item.reminderId || undefined,
    occurrenceAt: item.occurrenceAt || undefined,
    note: item.note ? String(item.note).trim() : undefined,
    createdAt: item.createdAt || new Date().toISOString(),
    read: false,
    targetGone: false,
  };
  saveInbox(userEmail, workspaceId, [stored, ...items]);
  ClockAPI.addInboxItem(stored);
  return stored;
}

export async function pullRemoteInbox(userEmail, workspaceId) {
  const remote = await ClockAPI.listInbox();
  const items = remote?.items;
  if (!Array.isArray(items)) return loadInbox(userEmail, workspaceId);
  const merged = mergeInboxItems(loadInbox(userEmail, workspaceId), items)
    .filter((item) => !isInboxItemTombstoned(userEmail, workspaceId, item.id));
  saveInbox(userEmail, workspaceId, merged);
  return merged;
}

export function removeInboxItem(userEmail, workspaceId, id) {
  const items = loadInbox(userEmail, workspaceId);
  const removed = items.find((item) => item.id === id) || null;
  if (!removed) return { items, removed: null };
  const deletedIds = readDeletedIds(userEmail, workspaceId);
  deletedIds.add(id);
  writeDeletedIds(userEmail, workspaceId, deletedIds);
  const next = items.filter((item) => item.id !== id);
  saveInbox(userEmail, workspaceId, next);
  ClockAPI.deleteInboxItem(id);
  return { items: next, removed: { ...removed } };
}

export function restoreInboxItem(userEmail, workspaceId, item) {
  if (!item?.id) return loadInbox(userEmail, workspaceId);
  const deletedIds = readDeletedIds(userEmail, workspaceId);
  deletedIds.delete(item.id);
  writeDeletedIds(userEmail, workspaceId, deletedIds);
  const next = [
    item,
    ...loadInbox(userEmail, workspaceId).filter((row) => row.id !== item.id),
  ];
  saveInbox(userEmail, workspaceId, next);
  ClockAPI.replaceInboxItem(item);
  return next;
}

export function markInboxItemRead(userEmail, workspaceId, id) {
  const items = loadInbox(userEmail, workspaceId).map((item) =>
    item.id === id ? { ...item, read: true } : item
  );
  saveInbox(userEmail, workspaceId, items);
  ClockAPI.markRead(id);
  return items;
}

export function markAllInboxRead(userEmail, workspaceId) {
  const items = loadInbox(userEmail, workspaceId).map((item) => ({ ...item, read: true }));
  saveInbox(userEmail, workspaceId, items);
  ClockAPI.markAllRead();
  return items;
}

export function unreadInboxCount(items) {
  return (items || []).filter((item) => !item.read).length;
}

function goneKind(item, { campaignId, missionIds = [], campaignIds = [] }) {
  if ((campaignIds || []).includes(item.campaignId)) return 'campaign';
  if ((missionIds || []).includes(item.missionId)) {
    if (!campaignId || item.campaignId === campaignId) return 'mission';
  }
  return null;
}

export function markInboxTargetsGone(userEmail, workspaceId, detail) {
  const items = loadInbox(userEmail, workspaceId).map((item) => {
    const kind = goneKind(item, detail || {});
    return kind ? { ...item, targetGone: true, targetGoneKind: kind } : item;
  });
  saveInbox(userEmail, workspaceId, items);
  return items;
}

export function clearInboxTargetsGone(userEmail, workspaceId, detail) {
  const items = loadInbox(userEmail, workspaceId).map((item) => {
    const kind = goneKind(item, detail || {});
    if (!kind) return item;
    const next = { ...item, targetGone: false };
    delete next.targetGoneKind;
    return next;
  });
  saveInbox(userEmail, workspaceId, items);
  return items;
}
